<template>
  <v-container fluid class="mb-12 px-0 pt-0">
    <v-list class="pb-1 px-0">
      <v-list-item v-if="!isNaN(scanCompletion)" class="ma-0 px-2">
        <v-card-text class="text-center">{{ $t('subOrder.pickPackShipStep2Hints') }}</v-card-text>
      </v-list-item>
      <v-list-item v-if="!isNaN(scanCompletion)" class="ma-0 px-2">
        <v-list-item-content>
          <v-progress-linear :value="scanCompletion" :indeterminate="isStockLoading"></v-progress-linear>
        </v-list-item-content>
        <v-list-item-action>{{ scanCompletion + '%' }}</v-list-item-action>
      </v-list-item>
      <v-card class="my-1 mx-1" outlined v-for="(item, itemIndex) of itemsToScan" v-bind:key="itemIndex">
        <v-list-item class="px-0">
          <v-list-item-avatar
            :height="getListItemBinHeight(item.is_scanned)"
            :class="{
              'mr-1': true,
              'my-0': true,
              'fully-scanned-bin': item.is_scanned,
              'normal-bin': !item.is_scanned,
            }"
            width="20"
            min-width="0"
          >
            {{ item.bin_id }}
          </v-list-item-avatar>
          <v-list-item-avatar
            tile
            class="my-2"
            :size="getListItemAvatarsSizes(item.is_scanned, item.selected_product_positions)"
          >
            <v-img
              :class="{ 'fully-scanned-image': item.is_scanned }"
              :src="item.product_image_link || defaultImage"
            ></v-img>
          </v-list-item-avatar>
          <v-list-item-content class="py-2 pl-1">
            <v-list-item-subtitle :class="{ 'font-weight-bold': true, 'item-details-text': isMobile }">
              {{ item.product_title }}
            </v-list-item-subtitle>
            <v-list-item-subtitle :class="{ 'font-weight-bold': true, 'item-details-text': isMobile }">
              {{ item.product_sale_price | formatPrice(item.currency) }} {{ item.currency.code }}
            </v-list-item-subtitle>
            <v-list-item-action-text v-if="item.product_id" :class="isMobile ? 'item-details-text' : ''">
              {{ $t('product.ref') }}: {{ item.product_id }}
            </v-list-item-action-text>
            <v-list-item-action-text
              v-if="item.product_size && !item.is_scanned"
              :class="{ 'item-details-text': isMobile }"
            >
              {{ $t('product.size') }}: {{ item.product_size }}
            </v-list-item-action-text>
            <v-list-item-action-text
              v-if="item.product_color && !item.is_scanned"
              :class="{ 'item-details-text': isMobile }"
            >
              {{ $t('product.color') }}: {{ item.product_color }}
            </v-list-item-action-text>
            <v-skeleton-loader v-if="isStockLoading" max-width="160" type="text"></v-skeleton-loader>
            <v-list-item-action-text
              v-else-if="item.selected_product_positions && item.selected_product_positions.length"
              :class="{ 'item-details-text': isMobile }"
            >
              {{ formatPositionDisplay(item.selected_product_positions) }}
            </v-list-item-action-text>
            <!--              <span >-->
            <v-list-item-action-text
              v-else-if="loadedStocks[item.product_id] !== undefined && loadedStocks[item.product_id] !== null"
              :class="{ 'item-details-text': isMobile }"
            >
              {{ $t('product.availableStock') }}:
              <v-badge
                v-if="loadedStocks[item.product_id] && loadedStocks[item.product_id].stock_total !== undefined"
                inline
                dot
                left
                :color="getStockColor(loadedStocks[item.product_id].stock_total, 1)"
              >
                <span>{{ loadedStocks[item.product_id].stock_total }}</span>
              </v-badge>
            </v-list-item-action-text>
            <v-list-item-action-text
              v-if="
                loadedStocks[item.product_id] &&
                Array.isArray(loadedStocks[item.product_id].product_positions) &&
                loadedStocks[item.product_id].product_positions.length
              "
            >
              <div
                v-for="(productPosition, index) in loadedStocks[item.product_id].product_positions"
                v-bind:key="index"
                class="pl-1 position-detail-text"
              >
                <v-badge inline dot left :color="getStockColor(productPosition.stock, 1)">
                  <span class="pr-1">{{ productPosition.stock }}</span>
                  <span v-if="Array.isArray(productPosition.position_details)">
                    <span v-for="(positionDetail, index) in productPosition.position_details" v-bind:key="index">
                      {{ ' - ' + positionDetail.name }} {{ positionDetail.value }}
                    </span>
                  </span>
                </v-badge>
                <br />
              </div>
            </v-list-item-action-text>
          </v-list-item-content>
          <v-list-item-action class="align-right item-quantity-text pr-2">
            <v-list-item-action-text :class="{ 'is-scanned': item.is_scanned, 'item-details-text': isMobile }">
              <v-checkbox
                v-model="item.is_scanned"
                :disabled="isStockLoading"
                @change="updateSumScannedItem(item)"
              ></v-checkbox>
            </v-list-item-action-text>
          </v-list-item-action>
        </v-list-item>
      </v-card>
    </v-list>
    <product-position-choice-dialog
      v-if="targetItem"
      v-model="showProductPositionChoiceDialog"
      :choices="loadedStocks[targetItem.product_id].product_positions"
      :product="targetItem"
      @confirm="confirmSelectedPosition"
    ></product-position-choice-dialog>
  </v-container>
</template>
<script>
  import { EventBus } from '@/event-bus'
  import { mapState } from 'vuex'
  import ProductPositionChoiceDialog from '@/components/dialogs/ProductPositionChoiceDialog'

  export default {
    name: 'pickPackShipStep2',
    components: {
      ProductPositionChoiceDialog,
    },
    props: {
      subOrders: {
        type: Array,
        require: true,
      },
      savedItemsToScan: {
        type: Array,
      },
    },
    data() {
      return {
        defaultImage: require('@/assets/img/products/product_default.png'),
        scan: '',
        itemsToScan: [],
        sumScannedItem: 0,
        loadedStocks: [],
        isStockLoading: false,
        showProductPositionChoiceDialog: false,
        targetItem: undefined,
      }
    },
    async created() {
      this.addEventKeyDown()
      if (Array.isArray(this.savedItemsToScan) && this.savedItemsToScan.length) {
        this.itemsToScan = JSON.parse(JSON.stringify(this.savedItemsToScan))
        await this.getStock()
        this.decrementLoadedStocks()
      } else {
        this.buildItemsToScan()
        await this.getStock()
      }
    },
    computed: {
      ...mapState(['currentStore']),
      totalNbProduct: function () {
        let totalNbProduct = 0
        for (let subOrder of this.subOrders) {
          totalNbProduct += subOrder.items.reduce((a, b) => a + b.product_quantity, 0)
        }
        return totalNbProduct
      },
      scanCompletion: function () {
        return ((this.sumScannedItem / this.totalNbProduct) * 100).toFixed(0)
      },
      isMobile: function () {
        return this.$vuetify.breakpoint.width < 650
      },
    },
    methods: {
      addEventKeyDown: function () {
        document.addEventListener('keydown', this.convertSpecialCharToNumber)
      },
      removeEventKeyDown: function () {
        document.removeEventListener('keydown', this.convertSpecialCharToNumber)
      },
      convertSpecialCharToNumber: function (event) {
        let charCode = event.charCode ? event.charCode : event.keyCode
        if (charCode >= 48 && charCode <= 57) {
          this.scan += String.fromCharCode(charCode)
        } else if (charCode == 13 && this.scan.trim() != '') {
          this.checkItemsForScan(this.scan)
          this.scan = ''
        }
      },
      checkItemsForScan: function (ean) {
        let productExists = false
        for (let item of this.itemsToScan) {
          let barcode = item.product_ean || item.barcode
          if (barcode == ean) {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('product.productScanned'),
              color: 'success',
            })
            if (!item.is_scanned) {
              item.is_scanned = true
              productExists = true
              this.sumScannedItem++
              if (this.loadedStocks[item.product_id]?.stock_total) {
                this.loadedStocks[item.product_id].stock_total--
              }
              this.setProductPosition(item)
              break
            } else {
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('product.productAlreadyScanned'),
                color: 'error',
              })
            }
            productExists = true
          }
        }
        if (!productExists) {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('product.productScannedError'),
            color: 'error',
          })
        }
      },
      getListItemAvatarsSizes(itemIsScanned) {
        if (this.isMobile) {
          if (itemIsScanned) {
            return 40
          } else {
            return 60
          }
        } else if (itemIsScanned) {
          return 60
        } else {
          return 80
        }
      },
      getListItemBinHeight(itemIsScanned) {
        if (this.isMobile) {
          if (itemIsScanned) {
            return 70
          } else {
            return 100
          }
        } else if (itemIsScanned) {
          return 82
        } else {
          return 117
        }
      },
      updateSumScannedItem(item) {
        if (item.is_scanned) {
          this.sumScannedItem++
          if (!isNaN(this.loadedStocks[item.product_id]?.stock_total)) {
            this.loadedStocks[item.product_id].stock_total--
          }
          this.setProductPosition(item)
        } else {
          this.sumScannedItem--
          if (!isNaN(this.loadedStocks[item.product_id]?.stock_total)) {
            this.loadedStocks[item.product_id].stock_total++
          }
          if (
            Array.isArray(this.loadedStocks[item.product_id]?.product_positions) &&
            this.loadedStocks[item.product_id].product_positions.length
          ) {
            let selectPosition = this.loadedStocks[item.product_id].product_positions.find(
              (productPosition) =>
                productPosition.position_id === item.selected_product_positions[0].product_position_id
            )
            if (selectPosition) {
              selectPosition.stock++
            }
          }
          item.selected_product_positions = []
        }
      },
      confirmSelectedPosition(item) {
        this.addEventKeyDown()
        let selectedPosition = this.loadedStocks[item.product_id]?.product_positions?.find(
          (productPosition) => productPosition.position_id === item.selected_product_positions[0].product_position_id
        )
        if (selectedPosition) {
          selectedPosition.stock--
        }
      },
      async getStock() {
        this.isStockLoading = true
        let productRefs = []
        this.itemsToScan.forEach((item) => {
          if (item.product_id) {
            productRefs.push(item.product_id)
          }
        })
        const unifiedStockInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            per_page: 1000,
            location_id: this.currentStore,
            product_id: productRefs,
            fields: ['stock_total', 'product_positions'],
          },
        }
        try {
          const res = await this.$Amplify.API.get('stocks', '/stocks', unifiedStockInit)

          let loadedStocks = {}
          for (let stock of res.data) {
            stock.product_positions = stock.product_positions?.map((stockPosition) => {
              return {
                stock: stockPosition.stock_total,
                position_id: stockPosition.product_position_id,
                position_details: stockPosition.custom,
                barcode: stockPosition.product_position_barcode,
              }
            })
            loadedStocks[stock.product_id] = { ...stock }
          }

          this.loadedStocks = loadedStocks
        } catch (e) {
          console.log(e)
        } finally {
          this.stockIsLoading = false
          this.isStockLoading = false
        }
      },
      getStockColor(itemStock, itemQuantity) {
        let color = 'red'
        if (itemStock >= itemQuantity) {
          color = 'green'
        } else if (itemStock > 1 && itemStock < itemQuantity) {
          color = 'orange'
        }
        return color
      },
      formatPositionDisplay(itemSelectedPositions) {
        if (Array.isArray(itemSelectedPositions) && itemSelectedPositions.length) {
          // in bin summary we have only 1 position because item quantities are split for step 2, so we use index 0
          const itemPosition = itemSelectedPositions[0]
          if (Array.isArray(itemPosition.custom) && itemPosition.custom.length) {
            let displayText = this.$t('product.takenOn') + ' '
            for (let positionDetail of itemPosition.custom) {
              displayText +=
                (positionDetail.name === 'customPosition' ? '' : positionDetail.name) + ' ' + positionDetail.value + ' '
            }
            return displayText
          }
        }
      },
      openProductPositionModal(item) {
        this.removeEventKeyDown()
        this.targetItem = item
        this.showProductPositionChoiceDialog = true
      },
      setProductPosition(item) {
        if (
          Array.isArray(this.loadedStocks[item.product_id]?.product_positions) &&
          this.loadedStocks[item.product_id].product_positions.length
        ) {
          this.openProductPositionModal(item)
        }
      },
      buildItemsToScan: function () {
        let itemsToScan = []
        const computedSubOrders = JSON.parse(JSON.stringify(this.subOrders))
        for (let computedSubOrder of computedSubOrders) {
          for (let item of computedSubOrder.items) {
            for (let qty = 0; qty < item.product_quantity; qty++) {
              this.$set(item, 'is_scanned', false)
              this.$set(item, 'currency', computedSubOrder.currency)
              itemsToScan.push({
                bin_id: computedSubOrder.binId,
                order_id: computedSubOrder.order_id,
                ...JSON.parse(JSON.stringify(item)),
                product_quantity: 1,
                validated_quantity: 1,
              })
            }
          }
        }
        itemsToScan = itemsToScan.sort(function (a, b) {
          if (a.product_id > b.product_id) {
            return 1
          } else if (a.product_id < b.product_id) {
            return -1
          } else {
            return 0
          }
        })
        for (let itemIndex in itemsToScan) {
          this.$set(this.itemsToScan, itemIndex, itemsToScan[itemIndex])
        }
      },
      // Used via $refs from parent component (binSummary.vue)
      // Do not remove: called as this.$refs.step2.returnItemsToScan()
      returnItemsToScan: function () {
        return this.itemsToScan
      },
      decrementLoadedStocks() {
        for (let itemToScan of this.itemsToScan) {
          if (itemToScan.is_scanned) {
            this.sumScannedItem++
            // we decrement the stock total loaded from unified stock to make it match with already picked items
            if (this.loadedStocks[itemToScan.product_id]?.stock_total) {
              this.loadedStocks[itemToScan.product_id].stock_total--
            }
            if (itemToScan.selected_product_positions.length) {
              // we decrement the stock of the position loaded from unified stock to make it match with already picked items
              // we take index à as there is only on position by item to scan at this step
              const itemToScanProductPosition = itemToScan.selected_product_positions[0]
              let loadedStocksItemPosition = this.loadedStocks[itemToScan.product_id].product_positions.find(
                (pos) => pos.position_id === itemToScanProductPosition.product_position_id
              )
              if (loadedStocksItemPosition) {
                loadedStocksItemPosition.stock -= itemToScanProductPosition.taken_quantity
              }
            }
          }
        }
      },
    },
    beforeDestroy() {
      this.removeEventKeyDown()
    },
  }
</script>
<style scoped>
  .is-scanned {
    color: green !important;
  }
  .item-details-text {
    font-size: 10px;
  }
  item-details-text-important {
    font-size: 12px;
  }
  .position-detail-text {
    font-size: 11px;
  }
  .fully-scanned-image {
    opacity: 0.6;
    filter: grayscale(1);
  }
  .fully-scanned-bin {
    background-color: #c3c3c3;
    color: white;
    border-radius: 4px;
  }
  .normal-bin {
    background-color: black;
    color: white;
    border-radius: 4px;
  }
  .item-quantity-text {
    max-width: 210px;
    justify-content: flex-start;
  }
</style>
